import { useRouteMatch } from 'react-router'
import { useHistory } from 'react-router-dom'

import { downloadFile, useModalContext } from '@sketch/components'
import { IS_EMBEDDED } from '@sketch/constants'
import {
  ShareInfoFragment,
  useGetWorkspacesForDuplicationQuery,
  useGetWorkspaceQuery,
  usePinShareMutation,
  useUnpinShareMutation,
} from '@sketch/gql-types'
import {
  isDraftsRoute,
  isLibrariesRoute,
  isSharedWithMeRoute,
  isTemplatesRoute,
  LaunchSketchAlert,
  useGetMyDraftsProject,
  useLicenseAgreement,
  useRegisterCommandBarCommands,
  useUserSignedIn,
} from '@sketch/modules-common'
import { useToast } from '@sketch/toasts'

import { createCurrentDocumentCommands } from './createCurrentDocumentCommands'
import { useToggleSidebarTab } from '../components/ShareSidebarTabContext'
import { useComponentsState } from '../components/ComponentsStateContext'
import { useDocumentInfoModal } from '../hooks/useDocumentInfoModal'
import { useHideAnnotationDots } from '../../annotations/hooks'
import { useNotificationSubscription } from '../../annotations/operations'
import { getShowArchiveAction } from '../../storageItems/utils'
import { useCheckCurrentAuthorizationForWorkspace } from '../../sso/operations/useCheckCurrentAuthorizationForWorkspace'
import { getEligibleWorkspacesForDuplication } from '../../workspace/utils/getEligibleWorkspacesForDuplication/getEligibleWorkspacesForDuplication'
import { useDuplicateShareTo } from '../hooks/useDuplicateShareTo'
import { useDuplicateShareToDrafts } from '../hooks/useDuplicateShareToDrafts'
import { useGetShareDropdownOptions } from '../operations/useGetShareDropdownOptions'
import { isSketchVersionGreaterThan } from '../../versioning/utils'

const LICENSE_AGREEMENT_TEXT =
  'Read and agree to the License Agreement to duplicate this Document.'

interface CurrentDocumentCommandBarRegistrationProps {
  share: ShareInfoFragment
  versions: {
    current: { shortId: string }
    latest: { shortId: string }
  }
}

export const CurrentDocumentCommandBarRegistration = ({
  share,
  versions,
}: CurrentDocumentCommandBarRegistrationProps) => {
  const history = useHistory()
  const route = useRouteMatch()
  const { showModal } = useModalContext()
  const { toggle } = useToggleSidebarTab()
  const { showToast } = useToast()
  const isSignedIn = useUserSignedIn()
  const componentsState = useComponentsState()
  const [hideAnnotations, setHideAnnotations] = useHideAnnotationDots()
  const updateNotificationStatus = useNotificationSubscription()
  const { checkAuthorization } = useCheckCurrentAuthorizationForWorkspace()
  const { hasLicenseAgreement, showLicenseAgreementModal } =
    useLicenseAgreement({
      shareIdentifier: share.identifier,
    })
  const { openDocumentInfoModal } = useDocumentInfoModal({
    share,
    versions,
    workspaceIdentifier: share.workspace.identifier,
  })
  const { data: workspaceData } = useGetWorkspaceQuery({
    variables: { identifier: share.workspace.identifier },
    fetchPolicy: 'cache-first',
    skip: !share.workspace.identifier || !share.workspace.userIsMember,
  })
  const [pinShare] = usePinShareMutation({
    variables: { shareIdentifier: share.identifier },
    onError: 'show-toast',
  })
  const [unpinShare] = useUnpinShareMutation({
    variables: { shareIdentifier: share.identifier },
    onError: 'show-toast',
  })

  const workspace = workspaceData?.workspace
  const isActive = workspace?.status === 'ACTIVE'
  const isArchived = !!share.archivedAt
  const versionIdentifier = share.version?.identifier || ''
  const isViewingLatestVersion =
    versions.current.shortId === versions.latest.shortId
  const showArchive = workspace
    ? getShowArchiveAction(workspace, share.project)
    : false
  const dropdownOptions = useGetShareDropdownOptions({
    share,
    version: versions.current,
  })
  const { data: workspacesForDuplicationData } =
    useGetWorkspacesForDuplicationQuery({
      skip: !isSignedIn || !isActive || isArchived || !versionIdentifier,
    })
  const draftsProject = useGetMyDraftsProject(share.workspace.identifier)
  const [duplicateShareToDrafts] = useDuplicateShareToDrafts({
    share,
    versionIdentifier,
    workspaceIdentifier: share.workspace.identifier,
  })
  const [duplicateShareTo] = useDuplicateShareTo({
    share,
    versionIdentifier,
    workspaceIdentifier: share.workspace.identifier,
  })

  const dropdownData =
    dropdownOptions.loading || dropdownOptions.error
      ? undefined
      : dropdownOptions.data
  const workspacesForDuplication = workspacesForDuplicationData?.me.workspaces
  const isInMyDraftsPage = isDraftsRoute(route.path)
  const isInMyDraftsProject = share.project?.projectType === 'PERSONAL_DRAFTS'
  const isLimitedDuplicateRoute =
    isLibrariesRoute(route.path) ||
    isSharedWithMeRoute(route.path) ||
    isTemplatesRoute(route.path)
  const isLibrary = share.shareType === 'LIBRARY'
  const isTemplate = share.shareType === 'TEMPLATE'

  const duplicationEligibility =
    workspacesForDuplication && dropdownData
      ? getEligibleWorkspacesForDuplication(
          workspacesForDuplication,
          dropdownData.share.workspace.identifier,
          dropdownData.share.publicAccessLevel,
          { hasAccessToWorkspaceCheckFn: checkAuthorization }
        )
      : undefined

  const currentWorkspace = duplicationEligibility?.currentWorkspace
  const userCanDuplicate =
    duplicationEligibility?.userCanDuplicate &&
    dropdownData?.share.userCanInspect &&
    dropdownData.document.sketchVersion &&
    isSketchVersionGreaterThan(dropdownData.document.sketchVersion, '43')
  const isLoggedInWithProperSSOCredentials = currentWorkspace
    ? checkAuthorization(currentWorkspace)
    : true
  const canDuplicate =
    isSignedIn &&
    isActive &&
    !isArchived &&
    !!versionIdentifier &&
    !!userCanDuplicate &&
    isLoggedInWithProperSSOCredentials
  const canDuplicateToSameLocation =
    canDuplicate &&
    !!currentWorkspace &&
    !isInMyDraftsProject &&
    !isLimitedDuplicateRoute &&
    !isLibrary &&
    !isTemplate
  const canDuplicateToDrafts = canDuplicate && !!draftsProject
  const duplicateToDraftsTitle =
    !canDuplicateToSameLocation && isInMyDraftsPage
      ? 'Duplicate'
      : 'Duplicate to My Drafts'
  const canDownload = dropdownData?.download.isAvailable || false
  const canOpenInSketch =
    !!dropdownData?.document.userCanOpenInApp &&
    isViewingLatestVersion &&
    !IS_EMBEDDED

  useRegisterCommandBarCommands(
    'current-document',
    () =>
      createCurrentDocumentCommands({
        canDownload,
        canOpenInSketch,
        componentsState,
        duplicateToDraftsTitle,
        history,
        isActive,
        isCommentsHidden: hideAnnotations,
        isTemplate,
        openDocumentInfoModal,
        onDownload: () => {
          const download = dropdownData?.download

          if (!download?.isAvailable) {
            return
          }

          if (hasLicenseAgreement) {
            showLicenseAgreementModal({
              agreeAction: () => downloadFile(download.url),
              helpText:
                'Read and agree to the License Agreement to download this Document.',
            })
            return
          }

          downloadFile(download.url)
        },
        onDuplicateToDrafts: canDuplicateToDrafts
          ? () =>
              showLicenseAgreementModal({
                agreeAction: duplicateShareToDrafts,
                helpText: LICENSE_AGREEMENT_TEXT,
              })
          : undefined,
        onDuplicateToSameLocation: canDuplicateToSameLocation
          ? () =>
              showLicenseAgreementModal({
                agreeAction: duplicateShareTo,
                helpText: LICENSE_AGREEMENT_TEXT,
              })
          : undefined,
        onOpenInSketch: () =>
          showModal(LaunchSketchAlert, {
            shareId: share.identifier,
            action: 'openInSketch',
          }),
        onToggleCommentsHidden: () => setHideAnnotations(!hideAnnotations),
        onTogglePin: () => {
          if (share.pinnedByCurrentUserAt) {
            unpinShare({ variables: { shareIdentifier: share.identifier } })
            return
          }

          pinShare({ variables: { shareIdentifier: share.identifier } })
        },
        onToggleSubscription: () =>
          updateNotificationStatus({
            shareIdentifier: share.identifier,
            state: share.subscriptionStatus === 'ON' ? 'OFF' : 'ON',
          }),
        workspace: share.workspace,
        share,
        showArchive,
        showModal,
        showToast,
        sidebarTabToggle: toggle,
        versions,
      }),
    [
      share.identifier,
      share.name,
      share.archivedAt,
      share.deletedAt,
      share.commentsEnabled,
      share.project?.identifier,
      share.project?.projectType,
      share.pinnedByCurrentUserAt,
      share.shareType,
      share.subscriptionStatus,
      share.userCanUpdateSettings,
      share.version?.identifier,
      share.version?.shortId,
      share.workspace.identifier,
      share.workspace.userRole,
      canDownload,
      canDuplicateToDrafts,
      canDuplicateToSameLocation,
      canOpenInSketch,
      duplicateToDraftsTitle,
      hideAnnotations,
      isActive,
      isTemplate,
      showArchive,
      versions.current.shortId,
      versions.latest.shortId,
    ]
  )

  return null
}
